import { ArrowUpRight, Sparkles } from "lucide-react";
import { site } from "../../lib/siteConfig";

const services = [
  {
    id: "affitti-brevi",
    title: "Affitti Brevi",
    subtitle: "Tutto sembra girare. Ma il margine?",
    image: "https://images.pexels.com/photos/20259352/pexels-photo-20259352.jpeg",
    features: [
      "Analisi di mercato sul tuo immobile",
      "Creazione annunci professionali",
      "Gestione online e burocrazia",
      "Pulizie e gestione operativa ospiti",
    ],
    note: "Gestione burocratica, online e operativa. Ma ricorda: serve una visione globale per comprendere la reale rendita netta.",
    highlight: false,
  },
  {
    id: "affitti-transitori",
    title: "Affitto Transitorio",
    subtitle: "La soluzione ideale per le grandi città.",
    image: "https://images.pexels.com/photos/6970056/pexels-photo-6970056.jpeg",
    features: [
      "Target studenti e lavoratori fuori sede",
      "Durata fino a 18 mesi massimo",
      "Margini più alti, sicurezza di pagamento",
      "Cedolare secca al 10% e sconto IMU*",
    ],
    note: "Alta richiesta di mercato, margini reali superiori e tassazione agevolata. Il vero punto di equilibrio.",
    highlight: true,
  },
  {
    id: "affitti-4-4",
    title: "Affitto 4+4",
    subtitle: "Costanza e tranquillità, con ritmo più lento.",
    image: "https://images.unsplash.com/photo-1645241910657-15bc4c16e0f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBsaXZpbmclMjByb29tJTIwY296eXxlbnwwfHx8fDE3NzY4NDA1MDF8MA&ixlib=rb-4.1.0&q=85",
    features: [
      "Soluzione stabile e a lungo termine",
      "Meno gestione, minori incombenze",
      "Inquilini selezionati e contratti tutelanti",
      "Ritorno costante nel tempo",
    ],
    note: "Margini più contenuti, ma ideale per chi cerca serenità e prevedibilità.",
    highlight: false,
  },
];

export const Services = () => {
  return (
    <section
      id="servizi"
      data-testid="services-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-white border-y border-brand-line/80"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 sm:mb-20">
          <div className="max-w-2xl">
            <p className="divider-ornament" data-testid="services-eyebrow">I 3 scenari di affitto</p>
            <h2
              data-testid="services-headline"
              className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] font-bold text-brand-ink mt-8"
            >
              Tre strade. Una<span className="italic font-normal"> scelta consapevole</span>.
            </h2>
          </div>
          <p className="max-w-md text-base sm:text-lg leading-relaxed text-brand-muted">
            Ogni proprietario ha obiettivi diversi. Scegliamo insieme la formula che rende di più,
            tutela l'immobile e semplifica la gestione.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((s, i) => (
            <article
              key={s.id}
              id={s.id}
              data-testid={`service-card-${s.id}`}
              className={`group relative flex flex-col rounded-sm overflow-hidden transition-all duration-500 hover:-translate-y-1 ${
                s.highlight
                  ? "bg-brand-ink text-white lg:scale-[1.03] shadow-2xl lg:-mt-4"
                  : "bg-brand-bg border border-brand-line"
              }`}
            >              {s.highlight && (
                <div
                  data-testid="service-highlight-badge"
                  className="absolute top-5 right-5 z-10 inline-flex items-center gap-1.5 bg-white text-brand-ink text-[11px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-sm"
                >
                  <Sparkles size={12} />
                  Consigliato
                </div>
              )}

              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className={`absolute inset-0 ${s.highlight ? "bg-brand-ink/20" : "bg-brand-ink/5"}`} />
              </div>

              <div className="flex flex-col flex-1 p-8 sm:p-10">
                <div className="text-xs uppercase tracking-[0.22em] font-semibold opacity-80">
                  {i + 1 < 10 ? `0${i + 1}` : i + 1} · Scenario
                </div>
                <h3 className={`mt-4 font-display text-3xl sm:text-[2rem] leading-tight font-bold ${s.highlight ? "text-white" : "text-brand-ink"}`}>
                  {s.title}
                </h3>
                <p className={`mt-2 text-base ${s.highlight ? "text-white/70" : "text-brand-muted"}`}>
                  {s.subtitle}
                </p>

                <ul className={`mt-7 space-y-3 border-t ${s.highlight ? "border-white/15" : "border-brand-line"} pt-6`}>
                  {s.features.map((f) => (
                    <li
                      key={f}
                      className={`flex items-start gap-3 text-sm leading-snug ${s.highlight ? "text-white/90" : "text-brand-text"}`}
                    >
                      <span className={`mt-1.5 h-1 w-4 shrink-0 ${s.highlight ? "bg-white" : "bg-brand-ink"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <p className={`mt-7 text-sm italic leading-relaxed ${s.highlight ? "text-white/80" : "text-brand-muted"}`}>
                  {s.note}
                </p>

                <div className="mt-8 pt-6 border-t border-opacity-30">
                  <a
                    href={site.calendly}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-cta-${s.id}`}
                    className={`inline-flex items-center gap-2 text-sm font-medium transition-colors ${
                      s.highlight ? "text-white hover:text-brand-beige" : "text-brand-ink hover:text-brand-inkHover"
                    }`}
                  >
                    Parla con Francesca
                    <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>

        <p
          data-testid="services-footnote"
          className="mt-10 text-xs italic text-brand-muted"
        >
          *Solo per i contratti a canone concordato.
        </p>
      </div>
    </section>
  );
};
